import { useEffect } from 'react';
import { setJWT, setActiveProfileId, clearAuthState, post as apiPost, put as apiPut, get as apiGet, setTokenRefreshHandler } from './apiService';
import { setActiveProfile, getById as getProfileById, patch as patchProfile } from './profileService';
import { setLogLevels } from './loggerService';
import { LoginResponse } from '../types/loginResponse';
import { User } from '../types/user';
import { useAuthStore } from '../store/useAuthStore';

const JWT_STORAGE_KEY = 'rev_admin_jwt';
const EMAIL_STORAGE_KEY = 'rev_admin_email';
const TOKEN_STORAGE_KEY = 'rev_admin_token';

// Attempt to refresh JWT using stored credentials
async function refreshToken(): Promise<boolean> {
  const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
  const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

  if (!storedEmail || !storedToken) {
    await logout();
    return false;
  }

  try {
    await loginWithToken(storedEmail, storedToken);
    return true;
  } catch {
    await logout();
    return false;
  }
}

// Component that checks for persisted auth on mount
export function InitAuth() {
  const { setAuthenticated, setLoading } = useAuthStore();

  useEffect(() => {
    // Register the token refresh handler for automatic 401 retry
    setTokenRefreshHandler(refreshToken);

    const restoreSession = async () => {
      const storedJwt = localStorage.getItem(JWT_STORAGE_KEY);
      if (storedJwt) {
        setJWT(storedJwt);
        try {
          await whoAmI();
          setAuthenticated(true);
        } catch {
          // JWT expired — try refresh with stored token
          const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
          const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
          if (storedEmail && storedToken) {
            try {
              await loginWithToken(storedEmail, storedToken);
              setAuthenticated(true);
            } catch {
              await logout();
            }
          } else {
            await logout();
          }
        }
      }
      setLoading(false);
    };

    restoreSession();

    return () => {
      setTokenRefreshHandler(null);
    };
  }, [setAuthenticated, setLoading]);

  return null;
}

// Login with email and password for admin users
export async function loginWithEmailAndPassword(
  email: string,
  password: string
): Promise<LoginResponse> {
  const response = await apiPost<LoginResponse>('/login?persona=company', {
    email,
    password,
    includeToken: true,
    includeSecret: true
  });

  // Persist auth credentials
  setJWT(response.jwt);
  localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
  localStorage.setItem(EMAIL_STORAGE_KEY, email);
  localStorage.setItem(TOKEN_STORAGE_KEY, response.token);

  // Load profile
  await whoAmI();

  return response;
}

// Refresh auth using stored token
export async function loginWithToken(
  email: string,
  token: string
): Promise<LoginResponse> {
  const response = await apiPost<LoginResponse>('/login-token', { email, token, includeToken: true });

  // Persist refreshed credentials
  setJWT(response.jwt);
  localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
  localStorage.setItem(TOKEN_STORAGE_KEY, response.token);

  // Load profile
  await whoAmI();

  return response;
}

// Logout - clears profile, JWT, and persisted state
export async function logout(): Promise<void> {
  setActiveProfile(null);
  clearAuthState();
  localStorage.removeItem(JWT_STORAGE_KEY);
  localStorage.removeItem(EMAIL_STORAGE_KEY);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  useAuthStore.getState().setAuthenticated(false);
}

// Get current user info - sets log levels and active profile
export async function whoAmI(profileId?: string): Promise<void> {
  const user = await apiGet<User>('/who-am-i');
  setLogLevels(user.logLevels);

  // Find profile by ID if provided, otherwise find company persona profile
  const activeProfileToSet = profileId
    ? user.profiles.find((p: { id: string }) => p.id === profileId)
    : user.profiles.find((p: { persona: string }) => p.persona === 'company');

  if (!activeProfileToSet) {
    throw new Error('Unauthorized');
  }

  setActiveProfileId(activeProfileToSet.id);

  // Fetch full profile data
  const profile = await getProfileById(activeProfileToSet.id);
  setActiveProfile(profile);

  // Check if timezone is set, if not set it to browser timezone
  if (!profile.timezone) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    profile.timezone = browserTimezone;

    await patchProfile({
      id: profile.id,
      timezone: browserTimezone,
    });
  }
}

// Send access code to email (no JWT required)
export async function sendAccessCode(email: string): Promise<void> {
  await apiPut<void>(`/access-code/email?email=${encodeURIComponent(email)}`, {});
}

// Login with emailed access code (no JWT required)
export async function loginWithCode(email: string, code: string): Promise<LoginResponse> {
  const response = await apiPost<LoginResponse>('/login-code?persona=company', {
    email,
    password: code,
    includeToken: true,
  });

  setJWT(response.jwt);
  localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
  localStorage.setItem(EMAIL_STORAGE_KEY, email);
  localStorage.setItem(TOKEN_STORAGE_KEY, response.token);

  await whoAmI();
  return response;
}

// Set new password (requires JWT)
export async function setNewPassword(email: string, password: string): Promise<void> {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  await apiPost<void>('/set-password', { email, token, password });
}

// Admin impersonate - get login secret for a profile
export async function impersonateProfile(profileId: string): Promise<{ secret: string }> {
  return apiGet<{ secret: string }>(`/admin/login-profile/${profileId}`);
}

// Convenience export
export const authService = {
  logout,
  loginWithEmailAndPassword,
  loginWithToken,
  whoAmI,
  sendAccessCode,
  loginWithCode,
  setNewPassword,
};
